import {
	Paper,
	styled,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
} from "@mui/material";
import { types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { getComparator, Order, stableSort } from "helpers/tableComparator";
import { DELETE_FAIL } from "messages";
import React, { useEffect, useState } from "react";
import { ProgressTableHead } from "./ProgressTableHead";
import { ProgressTableRow } from "./ProgressTableRow";
import * as tableTypes from "./types";

const PREFIX = "ProgressTable";

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	table: `${PREFIX}-table`,
	hide: `${PREFIX}-hide`,
	button: `${PREFIX}-button`,
	errBtn: `${PREFIX}-errBtn`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
	},
	[`& .${classes.paper}`]: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.table}`]: {
		minWidth: 750,
	},

	[`& .${classes.hide}`]: {
		display: "none",
	},

	[`& .${classes.button}`]: {
		float: "left",
		margin: "10",
	},

	[`& .${classes.errBtn}`]: {
		color: theme.palette.error.main,
	},
}));

export interface ProgressTableProps {
	trackUser?: types.UserWithProgress;
}

export const ProgressTable = ({
	trackUser,
}: ProgressTableProps): JSX.Element => {
	const [userActivities, setActivities] = useState<tableTypes.UserActivity[]>(
		[],
	);
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] =
		useState<keyof tableTypes.UserActivity>("activity");
	const [selected, setSelected] = useState<string[]>([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [err, setErr] = useState<boolean>(false);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof tableTypes.UserActivity,
	): void => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		if (event.target.checked) {
			const newSelecteds = userActivities.map((n) => n.activity);
			setSelected(newSelecteds);
			return;
		}
		setSelected([]);
	};

	const handleClick = (
		event: React.MouseEvent<unknown>,
		activity: string,
	): void => {
		const selectedIndex = selected.indexOf(activity);
		let newSelected: string[] = [];

		if (selectedIndex === -1) {
			// If the row does not been checked
			newSelected = [...selected, activity];
		} else if (selectedIndex === 0) {
			// If the row's selected record is at the begin of the list
			newSelected = [...selected.slice(1)];
		} else if (selectedIndex === selected.length - 1) {
			// If the row's selected record is at the end of the list
			newSelected = [...selected.slice(0, -1)];
		} else if (selectedIndex > 0) {
			// Other case when the row is selected
			newSelected = [
				...selected.slice(0, selectedIndex),
				...selected.slice(selectedIndex + 1),
			];
		}

		setSelected(newSelected);
	};

	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleDataToActivities = (user?: types.UserWithProgress): void => {
		const activities: tableTypes.UserActivity[] | null = [];

		if (!user?.assignments) {
			return;
		}

		for (const assignment of user?.assignments) {
			const assignmentName = assignment.name;

			for (const act of assignment.progress) {
				activities.push({
					activity: act.questName,
					assignment: assignmentName,
					accuracy: "",
					progress: act.status,
					time: "",
				});
			}
		}
		setActivities(activities);
	};

	useEffect(() => {
		handleDataToActivities(trackUser);
	}, [trackUser]);

	return (
		<Root className={classes.root}>
			<Paper className={classes.paper}>
				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						aria-label="enhanced table"
					>
						<ProgressTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={userActivities.length}
						/>
						<TableBody>
							{stableSort(userActivities, getComparator(order, orderBy))
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, idx) => {
									const key = `${row.assignment} ${row.activity} ${idx}`;

									return (
										<ProgressTableRow
											selected={selected}
											handleClick={handleClick}
											row={row}
											key={key}
											labelId={key}
										/>
									);
								})}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={userActivities.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				severity="error"
				message={DELETE_FAIL}
			/>
		</Root>
	);
};
